import { zodResolver } from '@hookform/resolvers/zod';
import { currentUserSchema, updateAccountSchema, updateEmailSchema } from '@hearth/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { FormField } from '@/components/form-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiRequest } from '@/lib/api';
import { applyServerErrors } from '@/lib/forms';
import { dateTime } from '@/lib/format';
import { sessionQuery, useAuthenticatedSession } from '@/lib/session';

export const Route = createFileRoute('/_app/account/')({ component: ProfilePage });

function ProfilePage() {
  return (
    <div className="grid gap-6">
      <IdentityCard />
      <div className="grid gap-6 lg:grid-cols-2">
        <UsernameCard />
        <EmailCard />
      </div>
    </div>
  );
}

/** Who is signed in, at a glance, with the security state people check most. */
function IdentityCard() {
  const { user } = useAuthenticatedSession();
  return (
    <Card className="flex-row flex-wrap items-center gap-4 px-5 py-4">
      <span
        aria-hidden
        className="bg-primary/12 text-primary grid size-14 place-items-center rounded-full text-lg font-semibold uppercase"
      >
        {user.username.slice(0, 2)}
      </span>
      <div className="grid min-w-0 gap-1">
        <p className="flex flex-wrap items-center gap-2 text-lg font-semibold tracking-tight">
          <span className="truncate">{user.username}</span>
          {user.isAdmin && <Badge variant="info">Administrator</Badge>}
        </p>
        <p className="text-muted-foreground truncate text-sm">{user.email}</p>
      </div>
      <dl className="text-muted-foreground ml-auto flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <div>
          <dt className="text-xs">Two-factor sign-in</dt>
          <dd className={user.twoFactorEnabled ? 'text-success font-medium' : 'font-medium'}>
            {user.twoFactorEnabled ? 'On' : 'Off'}
          </dd>
        </div>
        <div>
          <dt className="text-xs">Member since</dt>
          <dd className="text-foreground font-medium">{dateTime(user.createdAt)}</dd>
        </div>
      </dl>
    </Card>
  );
}

function UsernameCard() {
  const { user } = useAuthenticatedSession();
  const queryClient = useQueryClient();
  type Values = z.input<typeof updateAccountSchema>;
  const form = useForm<Values>({
    resolver: zodResolver(updateAccountSchema),
    values: { username: user.username },
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/client/account', { method: 'PATCH', body, schema: currentUserSchema }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      toast.success('Username updated.');
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Username</CardTitle>
        <CardDescription>
          Shown to people you share servers with and used to sign in.
        </CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent>
          <FormField label="Username" error={form.formState.errors.username?.message}>
            <Input autoComplete="username" {...form.register('username')} />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
            {save.isPending && <Loader2 className="animate-spin" />} Save
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function EmailCard() {
  const { user } = useAuthenticatedSession();
  const queryClient = useQueryClient();
  type Values = z.input<typeof updateEmailSchema>;
  const form = useForm<Values>({
    resolver: zodResolver(updateEmailSchema),
    values: { email: user.email, currentPassword: '' },
  });
  const save = useMutation({
    mutationFn: (body: Values) =>
      apiRequest('/api/client/account/email', { method: 'PUT', body, schema: currentUserSchema }),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      form.reset({ email: updated.email, currentPassword: '' });
      toast.success(`Email updated. Check ${updated.email} for a verification link.`);
    },
    onError: (err) => applyServerErrors(err, form.setError),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Email address
          {user.emailVerified ? (
            <Badge variant="secondary">Verified</Badge>
          ) : (
            <Badge variant="outline">Unverified</Badge>
          )}
        </CardTitle>
        <CardDescription>Used for sign-in, password resets and security notices.</CardDescription>
      </CardHeader>
      <form onSubmit={form.handleSubmit((v) => save.mutate(v))} noValidate>
        <CardContent className="grid gap-4">
          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" autoComplete="email" {...form.register('email')} />
          </FormField>
          <FormField
            label="Current password"
            error={form.formState.errors.currentPassword?.message}
          >
            <Input
              type="password"
              autoComplete="current-password"
              {...form.register('currentPassword')}
            />
          </FormField>
        </CardContent>
        <CardFooter className="mt-6 justify-end">
          <Button type="submit" disabled={save.isPending || !form.formState.dirtyFields.email}>
            {save.isPending && <Loader2 className="animate-spin" />} Change email
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}
